import { useMemo } from 'react';

import { LobbyTrendingEventsQueryVariables } from '@/api/events/query.generated';
import { useGetLobbyContentQuery } from '@/api/lobby/query.generated';
import { sortLobbyPlayers } from '@/utils/sortPlayers';
import { UseQueryArgs } from 'urql';

import { generateLobbyPlayerCards } from '../utils/lobbyPlayers';
import { useLobbyQueryVariables } from './use-lobby-query-variables';

/**
 * TODO: !!!
 *
 * Move this `useFantasyLobbyQueryWithVariables` inside the `LobbyDataProvider` and make private.
 * Since all data is fetched there anyway, export from the LobbyDataProvider also :
 *  - popular players
 *  - lobby players
 * This means we can remove the `usePopularPlayers` and `useLobbyPlayers` hooks,
 * and use directly our `useLobbyData`, and we will end up fetching the cached data from our hook
 * and data provider, instead of the URQL cache via `cache-only` as we currently do, witch seems to
 * cause us various issues .
 */

/**
 * !! DO NOT USE THIS HOOK DIRECTLY !! use `useLobbyData()` to prevent duplicate requests.
 *
 * React hook that wraps `useGetLobbyContentQuery` and automatically injects
 * the set of variables produced by `useLobbyQueryVariables`
 *
 * ! `urql` determines a query’s cache key from the GraphQL document and the variables that are supplied with it.
 *
 * @param options – `urql` `UseQueryArgs` except `variables` (those are fixed internally).
 *                  Pass `pause: true` to own the request explicitly, see `LobbyDataProvider`.
 */
export const useFantasyLobbyQueryWithVariables = (
    options: Omit<UseQueryArgs<LobbyTrendingEventsQueryVariables>, 'query' | 'variables'> = {
        requestPolicy: 'cache-first',
    }
) => {
    const lobbyQueryVariables = useLobbyQueryVariables();

    return useGetLobbyContentQuery({
        ...options,
        variables: lobbyQueryVariables,
        pause: options.pause ?? false,
    });
};

/**
 * @deprecated - see comment at top of file
 * Hook returning the popular players, as returned by 'getTopTrendingPlayers' query
 */
export const usePopularPlayers = (options: Omit<UseQueryArgs<LobbyTrendingEventsQueryVariables>, 'query'>) => {
    const [{ data, fetching }, execute] = useFantasyLobbyQueryWithVariables(options);

    const popularPlayers = useMemo(
        () => generateLobbyPlayerCards(data?.getTopTrendingPlayersData ?? []),
        [data?.getTopTrendingPlayersData]
    );

    return {
        popularPlayers,
        fetching: fetching || !data,
        hasNoData: !data || popularPlayers.length === 0,
        execute,
    };
};

/**
 * @deprecated - see comment at top of file
 * Hook returning the trending lobby players as returned by `getUpcomingLobbyEventsV2` query.
 */
export const useLobbyPlayers = (options: Omit<UseQueryArgs<LobbyTrendingEventsQueryVariables>, 'query'>) => {
    const [{ data, fetching }, refetchLobbyPlayers] = useFantasyLobbyQueryWithVariables(options);

    const trendingPlayers = useMemo(() => {
        // TODO: remove this sort when BE will send the sorted trending players
        return generateLobbyPlayerCards(data?.getUpcomingLobbyEventsV2 ?? []).sort(sortLobbyPlayers);
    }, [data?.getUpcomingLobbyEventsV2]);

    return {
        trendingPlayers,
        fetching: fetching || !data,
        refetchLobbyPlayers,
        hasNoData: !data && trendingPlayers.length === 0,
    };
};
